import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, PageHeader } from '../../components/shared/ui';
import { adminAPI } from '../../utils/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Cell, PieChart, Pie, Legend,
} from 'recharts';

const COLORS = ['#2563eb','#7c3aed','#0891b2','#059669','#d97706','#dc2626','#6b7280','#1d4ed8','#7e22ce','#0e7490','#065f46','#92400e'];

const SectionTitle = ({ children }) => (
  <h2 style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #e5e7eb' }}>{children}</h2>
);

const Analytics = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [llmStats, setLlmStats] = useState(null);

  useEffect(() => {
    adminAPI.getAnalytics().then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    import('../../utils/api').then(m => m.default.get('/admin/llm-stats')).then(r => setLlmStats(r.data)).catch(() => {});
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Yükleniyor...</div>;
  if (!data) return <div style={{ padding: 40, color: '#dc2626' }}>Veri alınamadı.</div>;

  const matchDist = [
    { name: '≥%90 (Mükemmel)', value: +data.match_distribution?.score_90 || 0, color: '#059669' },
    { name: '%75–90 (Yüksek)',  value: +data.match_distribution?.score_75 || 0, color: '#2563eb' },
    { name: '%50–75 (Orta)',    value: +data.match_distribution?.score_50 || 0, color: '#d97706' },
    { name: '<%50 (Düşük)',     value: +data.match_distribution?.score_low || 0, color: '#dc2626' },
  ].filter(d => d.value > 0);

  const gradeLabels = { '1': '1. Sınıf', '2': '2. Sınıf', '3': '3. Sınıf', '4': '4. Sınıf', '5': '5. Sınıf (Çift Anadal)' };

  const it = data.internship_types || {};
  const internshipTiles = [
    { name: 'Zorunlu',           value: +it.zorunlu || 0,       color: '#dc2626', bg: '#fef2f2' },
    { name: 'Gönüllü',           value: +it.gonullu || 0,       color: '#059669', bg: '#f0fdf4' },
    { name: 'Her ikisi de olur', value: +it.her_ikisi || 0,     color: '#2563eb', bg: '#eff6ff' },
    { name: 'Belirtilmemiş',     value: +it.belirtilmemis || 0, color: '#9ca3af', bg: '#f9fafb' },
  ];
  const internshipPie = internshipTiles.filter(d => d.name !== 'Belirtilmemiş' && d.value > 0);

  return (
    <>
      <PageHeader title="Analitik" description="Öğrenci demografisi ve eşleştirme istatistikleri" />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>

        {/* Üniversite Dağılımı */}
        <Card>
          <SectionTitle>🎓 Üniversite Dağılımı (Top 12)</SectionTitle>
          {data.universities.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: 32 }}>Henüz veri yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={data.universities} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={150} />
                <Tooltip formatter={(v) => [`${v} öğrenci`]} />
                <Bar dataKey="count" name="Öğrenci" radius={[0, 4, 4, 0]}>
                  {data.universities.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Şehir Dağılımı */}
        <Card>
          <SectionTitle>📍 Şehir Dağılımı (Top 12)</SectionTitle>
          {data.cities.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: 32 }}>Henüz veri yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={data.cities} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={100} />
                <Tooltip formatter={(v) => [`${v} öğrenci`]} />
                <Bar dataKey="count" name="Öğrenci" radius={[0, 4, 4, 0]}>
                  {data.cities.map((_, i) => <Cell key={i} fill={COLORS[(i + 3) % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>

        {/* Sınıf Dağılımı */}
        <Card>
          <SectionTitle>📚 Sınıf Dağılımı</SectionTitle>
          {data.grades.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: 32 }}>Henüz veri yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={data.grades.map(g => ({ ...g, name: gradeLabels[g.name] || `${g.name}. Sınıf`, value: +g.count }))}
                  cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, percent }) => `${name} %${(percent*100).toFixed(0)}`} labelLine={false}>
                  {data.grades.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => [`${v} öğrenci`]} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Eşleştirme Skoru Dağılımı */}
        <Card>
          <SectionTitle>🎯 Eşleştirme Skoru Dağılımı</SectionTitle>
          <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
            <div style={{ textAlign: 'center', flex: 1, padding: '12px 8px', background: '#f9fafb', borderRadius: 8 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#2563eb' }}>{data.match_distribution?.total || 0}</div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>Toplam Başvuru</div>
            </div>
            <div style={{ textAlign: 'center', flex: 1, padding: '12px 8px', background: '#f0fdf4', borderRadius: 8 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#059669' }}>{data.match_distribution?.avg_score || '—'}</div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>Ort. Eşleştirme Skoru</div>
            </div>
          </div>
          {matchDist.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: 24, fontSize: 13 }}>
              Henüz başvuru/eşleştirme verisi yok
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={matchDist}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip formatter={(v) => [`${v} başvuru`]} />
                <Bar dataKey="value" name="Başvuru" radius={[4, 4, 0, 0]}>
                  {matchDist.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      {/* Staj Türü Dağılımı (Zorunlu / Gönüllü) */}
      <Card style={{ marginBottom: 20 }}>
        <SectionTitle>📋 Staj Türü Talebi (Zorunlu / Gönüllü)</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'center' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {internshipTiles.map(t => (
              <div key={t.name} style={{ textAlign: 'center', padding: '16px 8px', background: t.bg, borderRadius: 8 }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: t.color }}>{t.value}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{t.name}</div>
              </div>
            ))}
          </div>
          {internshipPie.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: 32, fontSize: 13 }}>Henüz staj türü verisi yok</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={internshipPie} cx="50%" cy="50%" outerRadius={80} dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                  {internshipPie.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip formatter={(v) => [`${v} öğrenci`]} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      {/* En Yüksek Eşleşmeler */}
      <Card>
        <SectionTitle>🏆 En Yüksek Eşleşmeler</SectionTitle>
        {data.top_matches.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: 32, fontSize: 13 }}>
            Henüz eşleştirme verisi yok. İlanlar açıldığında ve başvurular geldiğinde burada görünecek.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  {['Öğrenci', 'Üniversite / Bölüm', 'Şirket / İlan', 'Eşleştirme Skoru', 'Durum'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.top_matches.map((m, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 500, color: '#111827' }}>
                      {m.first_name} {m.last_name}
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{m.email}</div>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#374151' }}>
                      <div>{m.university || '—'}</div>
                      <div style={{ color: '#9ca3af' }}>{m.department}</div>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: '#374151' }}>
                      <div style={{ fontWeight: 500 }}>{m.company_name}</div>
                      <div style={{ color: '#9ca3af' }}>{m.position_title}</div>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, background: '#e5e7eb', borderRadius: 4, height: 6, minWidth: 60 }}>
                          <div style={{ width: `${m.match_score}%`, background: m.match_score >= 75 ? '#059669' : m.match_score >= 50 ? '#d97706' : '#dc2626', height: '100%', borderRadius: 4 }} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: m.match_score >= 75 ? '#059669' : m.match_score >= 50 ? '#d97706' : '#dc2626' }}>%{m.match_score}</span>
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 20, fontWeight: 500,
                        background: m.status === 'accepted' ? '#f0fdf4' : m.status === 'invited' ? '#eff6ff' : '#f9fafb',
                        color: m.status === 'accepted' ? '#16a34a' : m.status === 'invited' ? '#2563eb' : '#6b7280' }}>
                        {m.status === 'accepted' ? 'Kabul' : m.status === 'invited' ? 'Davet' : m.status === 'rejected' ? 'Red' : m.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* AI Analiz Özeti */}
      {llmStats && (
        <>
          <SectionTitle>✦ AI CV Analizi Özeti</SectionTitle>

          {/* Genel sayılar */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'CV Yüklü', value: llmStats.overview?.total_with_cv, color: '#2563eb' },
              { label: 'AI Analiz Yapıldı', value: llmStats.overview?.total_analyzed, color: '#7c3aed' },
              { label: 'Junior', value: llmStats.overview?.junior_count, color: '#6b7280', level: 'junior' },
              { label: 'Mid-Level', value: llmStats.overview?.mid_count, color: '#2563eb', level: 'mid' },
              { label: 'Senior', value: llmStats.overview?.senior_count, color: '#7c3aed', level: 'senior' },
            ].map(item => (
              <Card key={item.label}
                onClick={item.level ? () => navigate(`/admin/students?llm_level=${item.level}`) : undefined}
                style={item.level ? { cursor: 'pointer' } : undefined}
                title={item.level ? `${item.label} seviyesindeki öğrencileri listele` : undefined}
              >
                <div style={{ fontSize: 28, fontWeight: 800, color: item.color }}>{item.value ?? '—'}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  {item.label}{item.level && <span style={{ color: '#2563eb', marginLeft: 5, fontWeight: 600 }}>↗</span>}
                </div>
              </Card>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
            {/* Seviye dağılımı */}
            {llmStats.levels?.length > 0 && (
              <Card>
                <h3 style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>Seviye Dağılımı</h3>
                <ResponsiveContainer width="100%" height={180}>
                  <PieChart>
                    <Pie data={llmStats.levels.map(l => ({ name: { junior: 'Junior', mid: 'Mid-Level', senior: 'Senior' }[l.level] || l.level, value: +l.count }))}
                      cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                      {llmStats.levels.map((_, i) => <Cell key={i} fill={['#6b7280','#2563eb','#7c3aed'][i % 3]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            )}

            {/* Son analizler */}
            {llmStats.recent?.length > 0 && (
              <Card>
                <h3 style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>Son 7 Günde Analiz</h3>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={llmStats.recent.map(r => ({ gün: new Date(r.day).toLocaleDateString('tr-TR', { day:'numeric', month:'short' }), Analiz: +r.count }))}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="gün" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="Analiz" fill="#7c3aed" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            )}
          </div>

          {/* En iyi öğrenciler */}
          {llmStats.top_students?.length > 0 && (
            <Card>
              <h3 style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 12 }}>En Yüksek Skorlu Adaylar (AI Analizi Yapılmış)</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {llmStats.top_students.map((st, i) => (
                  <div key={i} style={{ padding: '12px 14px', borderRadius: 8, background: '#f5f3ff', border: '1px solid #e9d5ff' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                      <div>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{st.first_name} {st.last_name}</span>
                        <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 8 }}>{st.university}</span>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {st.gpa && <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 20, background: '#eff6ff', color: '#2563eb' }}>GPA {st.gpa}</span>}
                        {st.llm_level && <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 20, background: '#ede9fe', color: '#7c3aed', fontWeight: 600 }}>{{ junior:'Junior', mid:'Mid', senior:'Senior' }[st.llm_level]}</span>}
                      </div>
                    </div>
                    <p style={{ fontSize: 11, color: '#4c1d95', margin: '0 0 6px', lineHeight: 1.5 }}>{st.llm_summary}</p>
                    {st.llm_strengths?.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {st.llm_strengths.map((s, j) => <span key={j} style={{ fontSize: 10, padding: '1px 6px', borderRadius: 20, background: '#d1fae5', color: '#065f46' }}>+ {s}</span>)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </>
  );
};

export default Analytics;
